import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';

export interface FollowItem {
  id: string;           // Firestore doc id
  userId: string;       // The user who is following
  targetId: string;     // The entity being followed (artist ID, label ID, etc.)
  targetType: 'artist' | 'label' | 'producer' | 'user';
  targetName: string;
  targetSlug?: string;  // For artists — the slug for linking
  targetImageUrl?: string;
  createdAt: Timestamp;
  // Denormalised info about the follower (stored when the follow is created)
  fromUserName?: string;
  fromUserImageUrl?: string;
}

/* ------------------------------------------------------------------ */
/*  Follow Request Types                                               */
/* ------------------------------------------------------------------ */

export type FollowRequestStatus = 'pending' | 'accepted' | 'declined';

export interface FollowRequest {
  id: string;
  fromUserId: string;
  fromUserName: string;
  fromUserImageUrl?: string;
  toUserId: string;
  status: FollowRequestStatus;
  createdAt: Timestamp;
  respondedAt?: Timestamp;
}

/* ------------------------------------------------------------------ */
/*  Core Follow CRUD                                                   */
/* ------------------------------------------------------------------ */

/**
 * Follow an entity (artist, label, producer, user)
 */
export async function followEntity(
  userId: string,
  target: Omit<FollowItem, 'id' | 'userId' | 'createdAt'>,
  fromUser?: { name?: string; imageUrl?: string }
): Promise<string> {
  const docRef = doc(collection(db, 'follows'));
  await setDoc(docRef, {
    userId,
    targetId: target.targetId,
    targetType: target.targetType,
    targetName: target.targetName,
    targetSlug: target.targetSlug || '',
    targetImageUrl: target.targetImageUrl || '',
    createdAt: Timestamp.now(),
    // Denormalised follower info so the target can display who followed them
    ...(fromUser?.name ? { fromUserName: fromUser.name } : {}),
    ...(fromUser?.imageUrl ? { fromUserImageUrl: fromUser.imageUrl } : {}),
  });
  return docRef.id;
}

/**
 * Unfollow an entity
 */
export async function unfollowEntity(followDocId: string): Promise<void> {
  await deleteDoc(doc(db, 'follows', followDocId));
}

/**
 * Unfollow by userId + targetId combo
 */
export async function unfollowByTargetId(userId: string, targetId: string): Promise<void> {
  const q = query(
    collection(db, 'follows'),
    where('userId', '==', userId),
    where('targetId', '==', targetId)
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    await deleteDoc(d.ref);
  }
}

/**
 * Check if a user follows a specific entity
 * Returns the follow doc ID if following, null otherwise
 */
export async function isFollowing(userId: string, targetId: string): Promise<string | null> {
  try {
    const q = query(
      collection(db, 'follows'),
      where('userId', '==', userId),
      where('targetId', '==', targetId)
    );
    const snap = await getDocs(q);
    return snap.empty ? null : snap.docs[0].id;
  } catch {
    return null;
  }
}

/**
 * Get all entities a user follows
 */
export async function getFollowing(userId: string): Promise<FollowItem[]> {
  try {
    const q = query(
      collection(db, 'follows'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowItem));
  } catch (error) {
    console.error('Error fetching follows (trying fallback without orderBy):', error);
    try {
      // Fallback without orderBy in case composite index is missing
      const fallbackQ = query(
        collection(db, 'follows'),
        where('userId', '==', userId)
      );
      const snap = await getDocs(fallbackQ);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as FollowItem))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
    } catch (err2) {
      console.error('Error fetching follows (fallback):', err2);
      return [];
    }
  }
}

/**
 * Get IDs of all entities a user follows (for quick filtering)
 */
export async function getFollowingIds(userId: string): Promise<Set<string>> {
  const follows = await getFollowing(userId);
  return new Set(follows.map(f => f.targetId));
}

/**
 * Subscribe to a user's follows in real-time
 */
export function subscribeFollowing(
  userId: string,
  callback: (follows: FollowItem[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'follows'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  let fallbackUnsub: Unsubscribe | null = null;

  const unsub = onSnapshot(q, (snap) => {
    const follows = snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowItem));
    callback(follows);
  }, (error) => {
    console.error('Error subscribing to follows (trying fallback without orderBy):', error);
    // Fallback without orderBy in case composite index is missing
    const fallbackQ = query(
      collection(db, 'follows'),
      where('userId', '==', userId)
    );
    fallbackUnsub = onSnapshot(fallbackQ, (snap) => {
      const follows = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as FollowItem))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      callback(follows);
    }, (err2) => {
      console.error('Error subscribing to follows (fallback):', err2);
      callback([]);
    });
  });

  return () => {
    unsub();
    fallbackUnsub?.();
  };
}

/** One-shot list of a user's followers (people who follow them). */
export async function getFollowers(userId: string): Promise<FollowItem[]> {
  const q = query(collection(db, 'follows'), where('targetId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowItem));
}

/**
 * Subscribe to a user's followers (people who follow them).
 * Returns FollowItem[] where targetId === userId.
 */
export function subscribeFollowers(
  userId: string,
  callback: (followers: FollowItem[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'follows'),
    where('targetId', '==', userId)
  );
  const unsub = onSnapshot(q, (snap) => {
    const followers = snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowItem));
    callback(followers);
  }, (error) => {
    console.error('Error subscribing to followers:', error);
    callback([]);
  });
  return unsub;
}

/**
 * Toggle follow: follow if not following, unfollow if following.
 * If the target profile is private, creates a follow request instead.
 */
export async function toggleFollow(
  userId: string,
  target: Omit<FollowItem, 'id' | 'userId' | 'createdAt'>,
  options?: { fromUserName?: string; fromUserImageUrl?: string }
): Promise<{ following: boolean; followId?: string; requested?: boolean }> {
  const existingId = await isFollowing(userId, target.targetId);
  if (existingId) {
    await unfollowEntity(existingId);
    return { following: false };
  }

  // Check if target user has a private profile
  if (target.targetType === 'user') {
    const isPrivate = await isProfilePrivate(target.targetId);
    if (isPrivate) {
      // Check for existing pending request
      const existing = await getFollowRequestStatus(userId, target.targetId);
      if (existing === 'pending') {
        return { following: false, requested: true };
      }
      // Create a follow request instead of direct follow
      await sendFollowRequest(
        userId,
        options?.fromUserName || 'User',
        options?.fromUserImageUrl || '',
        target.targetId
      );
      return { following: false, requested: true };
    }
  }

  // Public profile or non-user entity: follow directly
  const newId = await followEntity(userId, target, {
    name: options?.fromUserName,
    imageUrl: options?.fromUserImageUrl,
  });
  return { following: true, followId: newId };
}

/* ------------------------------------------------------------------ */
/*  Profile Privacy                                                    */
/* ------------------------------------------------------------------ */

/**
 * Check if a user's profile is set to private
 */
export async function isProfilePrivate(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      // isPublicProfile defaults to true if not set
      return data.isPublicProfile === false;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Update a user's public/private profile setting
 */
export async function setProfileVisibility(userId: string, isPublic: boolean): Promise<void> {
  const userDocRef = doc(db, 'users', userId);
  await setDoc(userDocRef, { isPublicProfile: isPublic }, { merge: true });
}

/* ------------------------------------------------------------------ */
/*  Follow Requests                                                    */
/* ------------------------------------------------------------------ */

/**
 * Send a follow request to a private profile user
 */
export async function sendFollowRequest(
  fromUserId: string,
  fromUserName: string,
  fromUserImageUrl: string,
  toUserId: string
): Promise<string> {
  // Check for existing pending request
  const existing = await getFollowRequestStatus(fromUserId, toUserId);
  if (existing === 'pending') throw new Error('Request already pending');

  const docRef = doc(collection(db, 'followRequests'));
  await setDoc(docRef, {
    fromUserId,
    fromUserName,
    fromUserImageUrl: fromUserImageUrl || '',
    toUserId,
    status: 'pending',
    createdAt: Timestamp.now(),
  });
  return docRef.id;
}

/**
 * Get the current status of a follow request between two users
 */
export async function getFollowRequestStatus(
  fromUserId: string,
  toUserId: string
): Promise<FollowRequestStatus | null> {
  try {
    const q = query(
      collection(db, 'followRequests'),
      where('fromUserId', '==', fromUserId),
      where('toUserId', '==', toUserId),
      where('status', '==', 'pending')
    );
    const snap = await getDocs(q);
    if (!snap.empty) return 'pending';
    return null;
  } catch {
    return null;
  }
}

/**
 * Get all pending follow requests for a user (incoming)
 */
export async function getIncomingFollowRequests(userId: string): Promise<FollowRequest[]> {
  try {
    const q = query(
      collection(db, 'followRequests'),
      where('toUserId', '==', userId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowRequest));
  } catch (error) {
    console.error('Error fetching follow requests:', error);
    return [];
  }
}

/**
 * Subscribe to incoming follow requests in real-time
 */
export function subscribeIncomingFollowRequests(
  userId: string,
  callback: (requests: FollowRequest[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'followRequests'),
    where('toUserId', '==', userId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as FollowRequest)));
  }, (error) => {
    console.error('Error subscribing to follow requests:', error);
    callback([]);
  });
}

/**
 * Accept a follow request — creates the follow relationship and marks request as accepted
 */
export async function acceptFollowRequest(request: FollowRequest): Promise<void> {
  // Create the follow relationship
  await followEntity(request.fromUserId, {
    targetId: request.toUserId,
    targetType: 'user',
    targetName: '', // Will be resolved when displayed
    targetImageUrl: '',
  });

  // Mark request as accepted
  const reqRef = doc(db, 'followRequests', request.id);
  await setDoc(reqRef, {
    status: 'accepted',
    respondedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Decline a follow request
 */
export async function declineFollowRequest(requestId: string): Promise<void> {
  const reqRef = doc(db, 'followRequests', requestId);
  await setDoc(reqRef, {
    status: 'declined',
    respondedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Cancel a sent follow request
 */
export async function cancelFollowRequest(fromUserId: string, toUserId: string): Promise<void> {
  const q = query(
    collection(db, 'followRequests'),
    where('fromUserId', '==', fromUserId),
    where('toUserId', '==', toUserId),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    await deleteDoc(d.ref);
  }
}
